import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  ArrowRight,
  Plug,
  Mic2,
  Zap,
  X as XIcon,
} from "lucide-react";

const SEEN_KEY = "be:welcome-seen-v1";

export function WelcomeModal({ userName }: { userName: string | null }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(SEEN_KEY) !== "1") {
      setOpen(true);
    }
  }, []);

  function close(): void {
    window.localStorage.setItem(SEEN_KEY, "1");
    setOpen(false);
  }

  if (!open) return null;

  const greeting = userName ? `Welcome, ${userName.split(" ")[0]}` : "Welcome";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm animate-fade-in"
      onClick={close}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-2xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gradient-to-br from-brand-50/80 via-white to-white p-6 sm:p-7">
          <button
            onClick={close}
            className="absolute right-4 top-4 text-ink-400 hover:text-ink-700"
          >
            <XIcon className="h-4 w-4" />
          </button>

          <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-md">
            <Sparkles className="h-5 w-5" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">
            {greeting} <span className="text-gradient">to BrandEcho</span>
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-ink-600">
            Write once. Echo across X, Bluesky, LinkedIn, Newsletter, and
            short-video — in your voice. Here's the 3-step path to your first
            post.
          </p>
        </div>

        <div className="space-y-2 px-6 pb-6 sm:px-7 sm:pb-7">
          <Step
            n={1}
            Icon={Zap}
            title="Start your 7-day free trial"
            desc="Pick a plan, enter a card. Cancel any time in 7 days — no charge."
          />
          <Step
            n={2}
            Icon={Plug}
            title="Connect a social account"
            desc="X, Bluesky, or LinkedIn — for publishing."
          />
          <Step
            n={3}
            Icon={Mic2}
            title="Train your voice + generate"
            desc="Paste 3-10 past posts. Then turn one idea into 5 native variants."
          />

          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-ink-200 pt-4">
            <p className="text-xs text-ink-500">
              You can always revisit this from the dashboard checklist.
            </p>
            <Link
              to="/pricing"
              onClick={close}
              className="btn-brand no-underline"
            >
              See plans
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({
  n,
  Icon,
  title,
  desc,
}: {
  n: number;
  Icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-ink-200/60 p-3.5">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-ink-100 text-ink-700">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink-900">
          <span className="mr-2 text-ink-400">{n}.</span>
          {title}
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-ink-500">{desc}</p>
      </div>
    </div>
  );
}
